import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import BowtieIcon from './BowtieIcon';
import ResumeDraft from './ResumeDraft';
import StateHierarchy from './StateHierarchy';
import Todos from './Todos';
import SecureForm from './SecureForm';
import IndustrySelectionForm from './IndustrySelectionForm';
import DocumentUploadForm from './DocumentUploadForm';
import BranchNavigator from './BranchNavigator';
import { apiService } from '../services/api';
import { Edit2, X, Check } from 'lucide-react';
import Tooltip from './Tooltip';
import styles from './ResumeInterview.module.css';

interface SecureFormField {
  field_id: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'url';
  placeholder?: string | null;
  required?: boolean;
  validation_pattern?: string | null;
}

interface SecureFormConfig {
  formId?: string;
  form_id?: string; // Alternative field name
  title: string;
  description: string;
  fields: SecureFormField[];
}

interface IndustryFormConfig {
  formId: string;
  title: string;
  description: string;
  industryGroups: string[];
  experienceOptions: number[];
}

interface DocumentUploadFormConfig {
  resumeInterviewId: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  timestamp?: Date;
  interface?: InterfaceConfig;
  userResponseForAnimation?: UserResponse;
  secureForm?: SecureFormConfig;
  industryForm?: IndustryFormConfig;
  documentUploadForm?: DocumentUploadFormConfig;
  toolName?: string;
  toolCallId?: string;
  isStreaming?: boolean;
}

interface InterfaceConfig {
  type: 'text' | 'buttons' | 'dropdown';
  options?: string[];
  placeholder?: string;
}

interface UserResponse {
  type: 'text' | 'button' | 'dropdown';
  text?: string;
  label?: string;
}

interface StateHierarchyComponent {
  id: string;
  name: string;
  type: string;
  metadata?: Record<string, any>;
}

interface StateHierarchyData {
  current_path: string;
  path_components: StateHierarchyComponent[];
  current_state_id: string;
}

interface TodoItem {
  todo_id: string;
  resume_interview_id: string;
  content: string;
  section: string;
  created_at: string;
  updated_at: string;
  status: 'pending' | 'completed';
  completed_at?: string;
}

interface ResumeInterviewProps {
  messages: Message[];
  currentInput: string;
  setCurrentInput: (value: string) => void;
  isLoading: boolean;
  hasStarted: boolean;
  currentInterface: InterfaceConfig;
  resumeDocument: string;
  isDraftPanelOpen: boolean;
  draftPanelWidth: number;
  isDraftPanelOpenWide: boolean;
  bowtieTop: number;
  bowtieIsSpinning: boolean;
  isAnimatingDownward: boolean;
  apiCallInProgressRef: React.MutableRefObject<boolean>;
  chatAreaRef: React.RefObject<HTMLDivElement | null>;
  lastMessageRef: React.MutableRefObject<HTMLDivElement | null>;
  autoScrollSpacerRef: React.RefObject<HTMLDivElement | null>;
  inputRef: React.RefObject<HTMLInputElement | null>;
  error: string | null;
  stateHierarchy: StateHierarchyData | null;
  todos: TodoItem[];
  currentStreamingMessageId: string | null;
  onSendResponse: (response: UserResponse) => void;
  onDraftPanelToggle: () => void;
  onDraftPanelResize: (width: number) => void;
  onDraftPanelToggleWide: () => void;
  onSecureFormSubmit: (formId: string, formData: Record<string, any>) => void;
  onIndustryFormSubmit: (formData: { industryGroup: string; yearsOfExperience: number }) => void;
  onUploadComplete?: (message: any) => void;
  onUploadError?: (error: string) => void;
  onUploadStart?: () => void;
  onUploadStageChange?: (stage: any) => void;
  activeVersionId?: string | null;
  onHistoryRestore?: () => void;
  onBranchSwitch?: (versionId: string) => void;
}

const ResumeInterview: React.FC<ResumeInterviewProps> = ({
  messages,
  currentInput,
  setCurrentInput,
  isLoading,
  hasStarted,
  currentInterface,
  resumeDocument,
  isDraftPanelOpen,
  draftPanelWidth,
  isDraftPanelOpenWide,
  bowtieTop,
  bowtieIsSpinning,
  isAnimatingDownward,
  apiCallInProgressRef,
  chatAreaRef,
  lastMessageRef,
  autoScrollSpacerRef,
  inputRef,
  error,
  stateHierarchy,
  todos,
  currentStreamingMessageId,
  onSendResponse,
  onDraftPanelToggle,
  onDraftPanelResize,
  onDraftPanelToggleWide,
  onSecureFormSubmit,
  onIndustryFormSubmit,
  onUploadComplete,
  onUploadError,
  onUploadStart,
  onUploadStageChange,
  activeVersionId,
  onHistoryRestore,
  onBranchSwitch
}) => {
  // State for editing messages
  const [editingMessageId, setEditingMessageId] = React.useState<string | null>(null);
  const [editingContent, setEditingContent] = React.useState('');
  const editInputRef = React.useRef<HTMLTextAreaElement>(null);
  
  // Handle editing
  const handleEditClick = (message: Message) => {
    setEditingMessageId(message.id);
    setEditingContent(message.content);
    // Focus the edit input after state update
    setTimeout(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    }, 100);
  };
  
  const handleEditCancel = () => {
    setEditingMessageId(null);
    setEditingContent('');
  };
  
  const handleEditSubmit = async (messageId: string) => {
    if (!editingContent.trim() || !activeVersionId) return;
    
    try {
      // Fork the conversation from this message
      const forkResponse = await apiService.forkResumeVersion(activeVersionId, messageId);
      
      // Switch to the new branch and send the edited message
      if (forkResponse.versionId) {
        // This should trigger a version switch in the parent component
        // We'll need to add this functionality
        onSendResponse({ 
          type: 'fork', 
          newVersionId: forkResponse.versionId,
          editedContent: editingContent.trim()
        } as any);
      }
      
      handleEditCancel();
    } catch (error) {
      console.error('Error forking conversation:', error);
      // Handle error appropriately
    }
  };
  
  // Render message
  const renderMessage = (message: Message, messageIndex: number) => {
    const isLastMessage = messageIndex === messages.length - 1;
    const isStreaming = message.id === currentStreamingMessageId;
    // Only show timestamp for user messages
    const shouldShowTimestamp = message.role === 'user' && !!message.timestamp;

    return (
      <div 
        key={message.id} 
        className={`${styles.message} ${styles[message.role]} ${isStreaming ? styles.streaming : ''}`}
        ref={isLastMessage ? lastMessageRef : null}
      >
        <div className={styles.messageContent}>
          {message.role === 'assistant' ? (
            <>
              {message.content && (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" />
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              )}
              {message.secureForm && (
                <SecureForm
                  formId={message.secureForm.formId || message.secureForm.form_id || ''}
                  title={message.secureForm.title}
                  description={message.secureForm.description}
                  fields={message.secureForm.fields}
                  onSubmit={onSecureFormSubmit}
                />
              )}
              {message.industryForm && (
                <IndustrySelectionForm
                  formId={message.industryForm.formId}
                  title={message.industryForm.title}
                  description={message.industryForm.description}
                  industryGroups={message.industryForm.industryGroups}
                  experienceOptions={message.industryForm.experienceOptions}
                  onSubmit={onIndustryFormSubmit}
                />
              )}
              {message.documentUploadForm && (
                <>
                  <DocumentUploadForm
                    resumeInterviewId={message.documentUploadForm.resumeInterviewId}
                    onUploadComplete={onUploadComplete}
                    onError={onUploadError}
                    onUploadStart={onUploadStart}
                    onStageChange={onUploadStageChange}
                  />
                </>
              )}
            </>
          ) : message.role === 'tool' ? (
            <div className={styles.toolMessage}>
              {message.content}
            </div>
          ) : (
            // User message with edit functionality
            <div className={styles.userMessageWrapper}>
              {editingMessageId === message.id ? (
                // Edit mode
                <div className={styles.editingWrapper}>
                  <textarea
                    ref={editInputRef}
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleEditSubmit(message.id);
                      } else if (e.key === 'Escape') {
                        handleEditCancel();
                      }
                    }}
                    className={styles.editTextarea}
                    rows={3}
                  />
                  <div className={styles.editActions}>
                    <Tooltip content="Save changes">
                      <button
                        onClick={() => handleEditSubmit(message.id)}
                        className={styles.editConfirmButton}
                        disabled={!editingContent.trim()}
                        aria-label="Submit edit"
                      >
                        <Check size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip content="Cancel editing">
                      <button
                        onClick={handleEditCancel}
                        className={styles.editCancelButton}
                        aria-label="Cancel edit"
                      >
                        <X size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              ) : (
                // Normal view
                <>
                  <p>{message.content}</p>
                  {!isLoading && !isAnimatingDownward && (
                    <Tooltip content="Edit message">
                      <button
                        onClick={() => handleEditClick(message)}
                        className={styles.editButton}
                        aria-label="Edit message"
                      >
                        <Edit2 size={14} />
                      </button>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          )}
          {shouldShowTimestamp && message.timestamp && (
            <span className={styles.timestamp}>
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Handle form submit
  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentInput.trim()) {
      onSendResponse({ type: 'text', text: currentInput.trim() });
    }
  };

  return (
    <div className={styles.resumeInterviewContainer}>
      {/* Work Area - contains state hierarchy, chat and resume draft */}
      <div className={styles.workArea}>
        {/* Left Panel - State Hierarchy (top) and Todos (bottom) */}
        <div className={styles.leftPanel}>
          <div className={styles.stateHierarchySection}>
            <StateHierarchy stateHierarchy={stateHierarchy} />
          </div>
          <div className={styles.todosSection}>
            <Todos todos={todos} />
          </div>
        </div>
        
        {/* Chat Area */}
        <div 
          className={styles.chatSection}
          style={{ 
            marginRight: (isDraftPanelOpen && !isDraftPanelOpenWide) ? `${draftPanelWidth}px` : '0',
            transition: 'margin-right 0.4s ease'
          }}
        >
          {/* Branch Navigator */}
          {activeVersionId && onBranchSwitch && (
            <BranchNavigator
              currentVersionId={activeVersionId}
              onBranchSwitch={onBranchSwitch}
            />
          )}
          
          <div className={styles.chatAreaWrapper} ref={chatAreaRef}>
            {hasStarted && (
              <div className={styles.singleBowtieContainer} style={{ top: `${bowtieTop}px` }}>
                <BowtieIcon 
                  width={36} 
                  height={36} 
                  className={bowtieIsSpinning ? styles.loadingBowtie : ''} 
                />
              </div>
            )}
            {messages.map((msg, msgIdx) => {
              return renderMessage(msg, msgIdx);
            })}
            <div ref={autoScrollSpacerRef} className={styles.autoScrollSpacer} />
          </div>
        </div>

        {/* Resume Draft Panel - inside work area */}
        <ResumeDraft
          document={resumeDocument}
          isOpen={isDraftPanelOpen}
          onToggle={onDraftPanelToggle}
          onResize={onDraftPanelResize}
          width={draftPanelWidth}
          isEmbedded={true}
          isOpenWide={isDraftPanelOpenWide}
          onToggleWide={onDraftPanelToggleWide}
          versionId={activeVersionId || undefined}
          onHistoryRestore={onHistoryRestore}
        />
      </div>

      {/* Input Area - separate from work area */}
      <div className={styles.inputArea}>
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}
        <div className={styles.inputAreaContent}>
          <form onSubmit={handleTextSubmit} className={styles.inputForm}>
            <div className={styles.inputContainer}>
              <input
                ref={inputRef}
                type="text"
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                placeholder={currentInterface.placeholder || "Type your response..."}
                className={styles.textInput}
                disabled={isLoading || isAnimatingDownward || apiCallInProgressRef.current}
              />
              <Tooltip content="Send message">
                <button
                  type="submit"
                  disabled={!currentInput.trim() || isLoading || isAnimatingDownward || apiCallInProgressRef.current}
                  className={styles.sendButton}
                  aria-label="Send message"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="19" x2="12" y2="5"/>
                    <polyline points="5 12 12 5 19 12"/>
                  </svg>
                </button>
              </Tooltip>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResumeInterview;
